import React, { useState } from 'react';
import { Input as AntInput, DatePicker } from 'antd';
import dayjs from 'dayjs';
import { t } from '../../i18n/i18n';
import './Input.scss';

const DATE_FORMAT = 'YYYY-MM-DD';

// `startPanel="year"` opens an empty picker on the year grid, so a date far from
// today (a date of birth) is year → month → day rather than dozens of « clicks.
function DateInput({ value, onChange, onReject, name, startPanel = 'date', className, ...props }) {
  const [mode, setMode] = useState('date');
  // Bumped to remount the picker after a refused date, so its text resets to
  // the field's actual value rather than keeping the date it just refused.
  const [resetKey, setResetKey] = useState(0);
  const dateValue = value ? dayjs(value) : null;

  /*
   * A typed date the picker rules out (`disabledDate`) is refused the moment
   * it is complete. antd never calls `onChange` for one — it leaves the text
   * sitting in the box, uncommitted — so this reads the box itself: a full
   * YYYY-MM-DD that `disabledDate` rejects resets the picker to the field's
   * real value and tells the parent why. A date picked from the calendar
   * can't get here; those days are greyed out.
   */
  const refuseTyped = (text) => {
    if (!props.disabledDate || !/^\d{4}-\d{2}-\d{2}$/.test(text)) return;
    const date = dayjs(text);
    if (!date.isValid() || !props.disabledDate(date)) return;
    setResetKey((k) => k + 1);
    onReject?.(date);
  };

  return (
    // `display: contents` — a hook for the input's key events, adding no box.
    <span
      style={{ display: 'contents' }}
      onKeyUp={(event) => refuseTyped(event.target.value ?? '')}
    >
      <DatePicker
        key={resetKey}
        className={className}
        classNames={{ popup: { root: 'reusable-antd-datepicker-popup' } }}
        value={dateValue}
        onChange={(date) => {
          // `disabledDate` only greys days in the calendar; a date typed through
          // the mask skipped it, so a date of birth in the future (or under the
          // minimum age) could still be entered by hand. Refuse it here too: the
          // field keeps its last good value, the box is cleared back to it, and
          // the parent is told why (`onReject`).
          if (date && props.disabledDate?.(date)) {
            setResetKey((k) => k + 1);
            onReject?.(date);
            return;
          }
          // A typed date lands while the year grid may still be up — show its month.
          if (date) setMode('date');
          if (onChange) {
            onChange({ target: { name, value: date ? date.format(DATE_FORMAT) : '' } });
          }
        }}
        // A mask keeps typing to digits in the YYYY-MM-DD slots — letters never land.
        format={{ format: DATE_FORMAT, type: 'mask' }}
        mode={mode}
        onPanelChange={(_, nextMode) => setMode(nextMode)}
        onOpenChange={(open) => {
          if (open) setMode(dateValue ? 'date' : startPanel);
        }}
        suffixIcon={<img src="/icons/metrics-calendar.svg" alt={t('Calendar')} style={{ width: '16px', height: '16px' }} />}
        {...props}
      />
    </span>
  );
}

export default function Input({
  label,
  type = 'text',
  placeholder,
  value,
  onChange,
  name,
  error,
  required,
  disabled,
  hint,
  min,
  max,
  step,
  variant = 'default',
  className = '',
  // Date fields: the message for a date the picker refuses (one
  // `disabledDate` rules out), given that date — e.g. "cannot be in the
  // future". Shown until a date is accepted.
  rejectMessage,
  ...props
}) {
  const [rejected, setRejected] = useState(null);
  const shownError = rejected ?? error;

  const renderInput = () => {
    if (type === 'password') {
      return (
        <AntInput.Password
          className={`reusable-antd-input ${variant === 'form' ? 'reusable-antd-input--form' : ''} ${className}`}
          placeholder={t(placeholder)}
          value={value}
          onChange={onChange}
          name={name}
          disabled={disabled}
          required={required}
          status={error ? 'error' : ''}
          {...props}
        />
      );
    }

    if (type === 'date') {
      return (
        <DateInput
          className={`reusable-antd-datepicker ${variant === 'form' ? 'reusable-antd-datepicker--form' : ''} ${className}`}
          placeholder={t(placeholder || 'Select date')}
          value={value}
          onChange={(event) => {
            setRejected(null);
            onChange?.(event);
          }}
          onReject={(date) =>
            setRejected(rejectMessage ? rejectMessage(date) : t('This date can’t be used'))
          }
          name={name}
          disabled={disabled}
          status={shownError ? 'error' : ''}
          {...props}
        />
      );
    }

    if (type === 'textarea') {
      return (
        <AntInput.TextArea
          className={`reusable-antd-textarea ${variant === 'form' ? 'reusable-antd-textarea--form' : ''} ${className}`}
          placeholder={t(placeholder)}
          value={value}
          onChange={onChange}
          name={name}
          rows={props.rows || 3}
          disabled={disabled}
          required={required}
          status={error ? 'error' : ''}
          {...props}
        />
      );
    }

    return (
      <AntInput
        type={type}
        className={`reusable-antd-input ${variant === 'form' ? 'reusable-antd-input--form' : ''} ${className}`}
        placeholder={t(placeholder)}
        value={value}
        onChange={onChange}
        name={name}
        min={min}
        max={max}
        step={step}
        disabled={disabled}
        required={required}
        status={error ? 'error' : ''}
        {...props}
      />
    );
  };

  return (
    <div className={`reusable-input-group ${variant === 'form' ? 'reusable-input-group--form' : ''} ${shownError ? 'has-error' : ''} ${disabled ? 'is-disabled' : ''}`}>
      {label && (
        <label className="reusable-input-label">
          {t(label)}
          {required && <span className="reusable-input-required">*</span>}
        </label>
      )}
      {renderInput()}
      {hint && <span className="reusable-input-hint">{t(hint)}</span>}
      {shownError && <span className="reusable-input-error-text">{t(shownError)}</span>}
    </div>
  );
}
